import React, { useState, useEffect, useMemo } from 'react';
import { SearchableSelect } from './SearchableSelect';
import { apiService } from '../services/apiService';
import { BarChart } from './charts/BarChart';
import { DEFAULT_COLORS } from '../constants';
import { Spinner } from './Spinner';

interface DashboardTimeViewProps {
    selectedCurrency: string | null;
    dateFrom: string | null;
    dateTo: string | null;
}

export const DashboardTimeView: React.FC<DashboardTimeViewProps> = ({ selectedCurrency, dateFrom, dateTo }) => {
    const [availableYears, setAvailableYears] = useState<{ id: number; name: string }[]>([]);
    const [selectedYears, setSelectedYears] = useState<{ id: number; name: string }[]>([]);
    const [yearlyData, setYearlyData] = useState<Record<string, { expenses: number; refunds: number }>>({});
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchYears = async () => {
            setIsLoading(true);
            const years = await apiService.getUniqueReceiptYears();
            const yearOptions = years.map(y => ({ id: y, name: String(y) }));
            setAvailableYears(yearOptions);
            
            if (years.length > 0) {
                const currentYear = new Date().getFullYear();
                const previousYear = currentYear - 1;
                
                const defaultSelection = yearOptions.filter(yo => 
                    yo.id === currentYear || yo.id === previousYear
                );
                
                if (defaultSelection.length > 0) {
                    setSelectedYears(defaultSelection);
                } else if (yearOptions.length > 0) {
                    setSelectedYears(yearOptions.slice(0, 2));
                }
            }
            setIsLoading(false);
        };
        fetchYears();
    }, []);

    useEffect(() => {
        const fetchExpenses = async () => {
            if (selectedYears.length > 0 && selectedCurrency) {
                setIsLoading(true);
                const yearNumbers = selectedYears.map(y => y.id);
                // Globální filtr data je zde záměrně ignorován (předáním null),
                // aby bylo možné provést srovnání celých roků bez ohledu na globální filtr.
                const data = await apiService.getYearlyMonthlyExpenses(yearNumbers, selectedCurrency, null, null);
                setYearlyData(data);
                setIsLoading(false);
            } else {
                setYearlyData({});
                if (selectedYears.length === 0) {
                    setIsLoading(false);
                }
            }
        };
        fetchExpenses();
    }, [selectedYears, selectedCurrency]);

    const barChartData = useMemo(() => {
        const months = Array.from({ length: 12 }, (_, i) =>
            new Date(2000, i).toLocaleString('cs-CZ', { month: 'short' })
        );

        return months.map((monthName, monthIndex) => {
            const cleanMonthName = monthName.endsWith('.') ? monthName.slice(0, -1) : monthName;
            const monthData: { month: string; [key: string]: any } = { month: cleanMonthName.charAt(0).toUpperCase() + cleanMonthName.slice(1) };

            selectedYears.forEach(year => {
                const key = `${year.name}-${monthIndex}`;
                const monthYearData = yearlyData[key] || { expenses: 0, refunds: 0 };
                // Sečteme výdaje a vratky (které jsou záporné) pro získání čisté částky
                const netAmount = (monthYearData.expenses || 0) + (monthYearData.refunds || 0);
                monthData[year.name] = netAmount;
            });
            return monthData;
        });
    }, [yearlyData, selectedYears]);


    const yearKeys = useMemo(() => selectedYears.map(y => y.name).sort(), [selectedYears]);
    const yearColors = useMemo(() => yearKeys.map((_, index) => DEFAULT_COLORS[index % DEFAULT_COLORS.length]), [yearKeys]);

    if (!selectedCurrency) {
        return <div className="text-center text-gray-500 py-10">Nejprve vyberte měnu v přehledu výše.</div>;
    }

    return (
        <div className="space-y-8">
            <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-2">
                    <h3 className="text-xl font-semibold text-gray-700">Měsíční výdaje ({selectedCurrency})</h3>
                    <div className="w-full sm:w-64">
                        <SearchableSelect
                            isMulti
                            options={availableYears}
                            value={selectedYears}
                            onChange={(v) => setSelectedYears(v as { id: number, name: string }[])}
                            placeholder="Vyberte roky..."
                        />
                    </div>
                </div>
                {isLoading ? (
                    <div className="flex justify-center items-center h-[300px]">
                        <Spinner text="Načítám data..." />
                    </div>
                ) : (
                     selectedYears.length > 0 ? (
                        <BarChart data={barChartData} keys={yearKeys} indexBy="month" colors={yearColors} currency={selectedCurrency} />
                     ) : (
                         <div className="text-center text-gray-500 py-10 h-[300px] flex items-center justify-center">Vyberte alespoň jeden rok pro zobrazení grafu.</div>
                     )
                )}
            </div>
        </div>
    );
};